import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Calendar, Tag, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { NewsItem } from '@/types/news';
import { dataService } from '@/lib/data/DataService';
import useEmblaCarousel from 'embla-carousel-react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [allArticles, setAllArticles] = useState<NewsItem[]>([]);
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const newsTitle = article?.title || 'Détail actualité médicale';
  const newsDescription = article?.description || article?.content?.slice(0, 150) || 'Détail d\'une actualité médicale en Mauritanie et Afrique du Nord.';

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) {
        navigate('/news');
        return;
      }

      try {
        const articleData = await dataService.getNewsById(id);
        if (articleData && articleData.status === 'published') {
          setArticle(articleData);
        } else {
          navigate('/news');
        }
        // Charger toutes les news pour la sidebar
        const all = await dataService.getNews();
        setAllArticles(all.filter(a => a.status === 'published'));
      } catch (error) {
        console.error('Erreur lors du chargement de l\'article:', error);
        navigate('/news');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id, navigate]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>{newsTitle} | LeaderProMed</title>
          <meta name="description" content={newsDescription} />
          <meta property="og:title" content={newsTitle + ' | LeaderProMed'} />
          <meta property="og:description" content={newsDescription} />
          <meta property="og:type" content="article" />
        </Helmet>
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>{newsTitle} | LeaderProMed</title>
          <meta name="description" content={newsDescription} />
          <meta property="og:title" content={newsTitle + ' | LeaderProMed'} />
          <meta property="og:description" content={newsDescription} />
          <meta property="og:type" content="article" />
        </Helmet>
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
            <Button onClick={() => navigate('/news')}>
              Retour aux actualités
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{newsTitle} | LeaderProMed</title>
        <meta name="description" content={newsDescription} />
        <meta property="og:title" content={newsTitle + ' | LeaderProMed'} />
        <meta property="og:description" content={newsDescription} />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
        {/* Hero Section */}
        {article && (
          <section className="relative h-[50vh] overflow-hidden">
            <img 
              src={article.images?.[0] || "/background.jpg"} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="text-center text-white px-4">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{article.title}</h1>
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Calendar size={16} />
                  <span>{new Date(article.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
              </div>
            </div>
          </section>
        )}
        {/* Content Section with Sidebar */}
        <main className="flex-grow">
          <section className="container mx-auto px-4 py-16">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <div className="lg:w-2/3">
                <Button 
                  variant="ghost" 
                  className="mb-8 flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  onClick={() => navigate('/news')}
                >
                  <ChevronLeft size={16} />
                  Retour aux actualités
                </Button>
                <article className="prose prose-lg max-w-none">
                  <div className="mb-6"></div>
                  <div
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: language === 'en' && article?.content_en && article.content_en.trim() !== '' ? article.content_en : article?.content || '' }}
                  />
                  {/* Affichage des miniatures d'images */}
                  {article.images && article.images.length > 0 && (
                    <div className="flex gap-2 mt-6 overflow-x-auto pb-2">
                      {article.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Image ${idx + 1}`}
                          className="h-20 w-28 object-cover rounded border border-gray-200 flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => setSelectedImage(img)}
                        />
                      ))}
                    </div>
                  )}
                  {/* Lightbox pour afficher l'image en grand */}
                  {selectedImage && (
                    <div
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
                      onClick={() => setSelectedImage(null)}
                    >
                      <div className="relative" onClick={e => e.stopPropagation()}>
                        <button
                          className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                          onClick={() => setSelectedImage(null)}
                        >
                          <X size={24} />
                        </button>
                        <img
                          src={selectedImage}
                          alt="Aperçu"
                          className="max-h-[80vh] max-w-[90vw] rounded shadow-lg"
                        />
                      </div>
                    </div>
                  )}
                  {/* Lien ou embed Facebook ou YouTube */}
                  {article?.facebookUrl ? (
                    <div className="my-8 flex justify-center">
                      <iframe
                        src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(article.facebookUrl)}&show_text=true&width=500`}
                        width="500"
                        height="600"
                        style={{ border: 'none', overflow: 'hidden' }}
                        scrolling="no"
                        frameBorder="0"
                        allowFullScreen={true}
                        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                        title="Facebook post embed"
                      ></iframe>
                    </div>
                  ) : article?.youtubeUrl ? (
                    <div className="my-8 flex justify-center">
                      <iframe
                        width="500"
                        height="281"
                        src={`https://www.youtube.com/embed/${article.youtubeUrl.split('v=')[1] || article.youtubeUrl.split('/').pop()}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : null}
                </article>
              </div>
              {/* Sidebar */}
              <div className="lg:w-1/3">
                <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="text-blue-600" size={18} />
                    <span className="uppercase text-xs font-semibold text-blue-600 tracking-wider">
                      {article.category === 'news' ? 'Actualités' : article.category === 'event' ? 'Événements' : article.category === 'interview' ? 'Interviews' : ''}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-blue-600 mb-6">Autres {article.category === 'news' ? 'Actualités' : article.category === 'event' ? 'Événements' : article.category === 'interview' ? 'Interviews' : ''}</h2>
                  <div className="space-y-6">
                    {allArticles
                      .filter(a => a.id !== article?.id && a.category === article?.category)
                      .slice(0, 5)
                      .map((item) => (
                        <Link 
                          key={item.id} 
                          to={`/news/${item.id}`}
                          className="block group"
                        >
                          <div className="flex gap-4 items-start">
                            <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-md">
                              <img 
                                src={item.images?.[0] || "/background.jpg"} 
                                alt={item.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 text-sm text-blue-600 mb-1">
                                <Calendar size={14} />
                                <span>{new Date(item.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                              </div>
                              <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                                {item.title}
                              </h3>
                            </div>
                          </div>
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default NewsDetail; 